import React, { useState, useEffect } from 'react';
import './App.css';
import Home from './pages/home';
import Demo from './pages/Demo';
import Privacy from './pages/privacy';
import TeamFeature from './pages/TeamFeature';
import CalendarConnection from './pages/CalendarConnection';
import Tasks from './pages/Tasks';
import CalendarPage from './pages/Calendar';
import Signup from './pages/Signup';
import Profile from './pages/Profile';
import Billing from './pages/Billing';
import MemberInvitation from './pages/MemberInvitation';
import ResetPassword from './pages/ResetPassword';
import PendingChanges from './pages/PendingChanges';
import Users from './pages/Users';
import TrialExpiredModal from './components/TrialExpiredModal';
import PaymentMethodRequiredModal from './components/PaymentMethodRequiredModal';
import SessionExpiredModal from './components/SessionExpiredModal';
import DailyTasksPopup from './components/DailyTasksPopup';
import AuthModal from './components/AuthModal';
import { useAuth } from './context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { UnpublishedChangesProvider } from './context/UnpublishedChangesContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import apiClient from './services/apiClient';



function GlobalSignInModal() {
  const { showSignInModal, setShowSignInModal } = useAuth();
  return <AuthModal showAuthModal={showSignInModal} setShowAuthModal={setShowSignInModal} />;
}

function App() {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  
  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async () => {
      try {
        // Try to get publishable key from backend first
        const response = await apiClient.get('/api/billing/config');
        let publishableKey = null;
        
        if (response.data.success && response.data.data && response.data.data.stripe && response.data.data.stripe.publishable_key) {
          publishableKey = response.data.data.stripe.publishable_key;
          console.log('✅ Using Stripe publishable key from backend configuration');
        } else {
          // Fallback to environment variable
          const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
          if (envKey && envKey !== 'YOUR_STRIPE_TEST_PUBLISHABLE_KEY_HERE' && (envKey.startsWith('pk_test_') || envKey.startsWith('pk_live_'))) {
            publishableKey = envKey;
            console.log('📝 Using Stripe publishable key from environment variable (development fallback)');
          }
        }
        
        if (publishableKey) {
          // Load Stripe with additional configuration for development
          const stripeOptions = {
            // Disable telemetry in development to avoid network issues
            telemetry: false,
          };
          
          const stripe = loadStripe(publishableKey, stripeOptions);
          setStripePromise(stripe);
          console.log('🔧 Stripe initialized with telemetry disabled for development');
        } else {
          console.error('❌ No valid Stripe publishable key found');
        }
      } catch (error) {
        console.warn('⚠️ Failed to initialize Stripe:', error.message);
        // Try environment variable as fallback
        const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (envKey && envKey !== 'YOUR_STRIPE_TEST_PUBLISHABLE_KEY_HERE' && (envKey.startsWith('pk_test_') || envKey.startsWith('pk_live_'))) {
          const stripeOptions = {
            telemetry: false, // Disable telemetry for development
          };
          const stripe = loadStripe(envKey, stripeOptions);
          setStripePromise(stripe);
          console.log('📝 Using Stripe publishable key from environment variable (fallback) with telemetry disabled');
        }
      }
    };
    initializeStripe();
  }, []);
  
  const handleProfileClick = () => setShowProfileModal(true);
  const handleProfileClose = () => setShowProfileModal(false);
  const handleBillingClick = () => setShowBillingModal(true);
  const handleBillingClose = () => setShowBillingModal(false);

  // Handle seats update callback
  const handleSeatsUpdate = (newSeatCount, updatedBillingData) => {
    // Call the global refresh function if it exists
    if (window.refreshCalendarSeats) {
      window.refreshCalendarSeats();
    }
  };

  // Helper to inject onProfileClick and onBillingClick to all pages with NavBar
  const withGlobalModals = (Component) => (props) => (
    <Component
      {...props}
      onProfileClick={handleProfileClick}
      onBillingClick={handleBillingClick}
      onSeatsUpdate={handleSeatsUpdate}
    />
  );

  return (
    <Router>
      <AuthProvider>
        <UnpublishedChangesProvider>
        <Elements stripe={stripePromise}>
          <Routes>
            <Route path="/" element={withGlobalModals(Home)()} />
            <Route path="/demo" element={withGlobalModals(Demo)()} />
            <Route path="/privacy" element={withGlobalModals(Privacy)()} />
            <Route path="/team-feature" element={withGlobalModals(TeamFeature)()} />
            <Route path="/register" element={withGlobalModals(CalendarConnection)()} />
            <Route path="/tasks" element={withGlobalModals(Tasks)()} />
            <Route path="/calendar" element={withGlobalModals(CalendarPage)()} />
            <Route path="/signup" element={withGlobalModals(Signup)()} />
            <Route path="/billing" element={withGlobalModals(Billing)()} />
            <Route path="/member-invitation/:token" element={<MemberInvitation />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pending-changes" element={withGlobalModals(PendingChanges)()} />
            <Route path="/users" element={withGlobalModals(Users)()} />
          </Routes>
          
          {/* Profile Modal */}
          {showProfileModal && (
            <div className="modal-backdrop show" style={{ display: 'block', zIndex: 1050 }}></div>
          )}
          <div className={`modal ${showProfileModal ? 'show' : ''}`} style={{ display: showProfileModal ? 'block' : 'none', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px', width: '90%' }}>
              <div className="modal-content">
                <Profile onClose={handleProfileClose} />
              </div>
            </div>
          </div>
          
          {/* Billing Modal - Now inside Elements provider */}
          {showBillingModal && (
            <div className="modal-backdrop show" style={{ display: 'block', zIndex: 1050 }}></div>
          )}
          <div className={`modal ${showBillingModal ? 'show' : ''}`} style={{ display: showBillingModal ? 'block' : 'none', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '700px', width: '90%' }}>
              <div className="modal-content">
                <Billing onClose={handleBillingClose} />
              </div>
            </div>
          </div>
          
          {/* Debug component to show data storage status */}
          {/* <DataStorageDebug /> */}
          
          {/* Trial Expired Modal */}
          <TrialExpiredModal />
          
          {/* Payment Method Required Modal */}
          <PaymentMethodRequiredModal />
          
          {/* Session Expired Modal */}
          <SessionExpiredModal />

          {/* Global Sign In Modal — opened by SessionExpiredModal */}
          <GlobalSignInModal />

          {/* Daily Tasks Popup — shown once per calendar day after login */}
          <DailyTasksPopup />
        </Elements>
        </UnpublishedChangesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
